import { ESRI_SPATIAL_REFERENCE, ESRI_MAP_UNIT_LAYER_CLUSTER_RADIUS } from '../configurations';
import { FEATURE_LAYERS_PROPERTIES } from '../constants';
import { FeatureLayer } from '../constructors';
import { getClusterConfig } from '../helpers/clusters';
import { eventIconLabels, eventIconRenderer } from '../helpers/events';
import { geoPointLocationIconRenderer } from '../helpers/geo-point-locations';
import { phoneCallIconLabel, phoneCallIconRenderer } from '../helpers/phone-calls';
import { polylineRenderer } from '../helpers/polylines';
import { stationIconLabels, stationIconRenderer } from '../helpers/stations';
import { hospitalIconRenderer } from '../helpers/hospitals';
import { unitIconLabels, unitIconRenderer } from '../helpers/units';
import { amlIconLabel, amlIconRenderer, amlLocationHistoryIconRenderer } from './aml';
import { getCircleSimpleFillRenderer } from './circles';
import { nearbyGeoCautionsIconRenderer } from './nearby-geo-cautions';
import { initialGeoPointPinRenderer } from './pin-initial-location';

/**
 * Helpers to create arcgis/esri FeatureLayer object
 */

export const createGeoPointLocationFeatureLayer = (): FeatureLayer => {
    return new FeatureLayer({
        title: FEATURE_LAYERS_PROPERTIES.GEO_POINT_LOCATIONS.title,
        source: [], // initialize with an empty collection
        renderer: geoPointLocationIconRenderer,
        objectIdField: 'objectId', // This must be defined when creating a layer from `Graphic` objects
        fields: [
            {
                name: 'entityId',
                alias: 'entityId',
                type: 'long',
            },
            {
                name: 'iconName',
                alias: 'iconName',
                type: 'string',
            },
        ],
        outFields: ['*'],
        geometryType: 'point',
        spatialReference: ESRI_SPATIAL_REFERENCE,
        layerId: FEATURE_LAYERS_PROPERTIES.GEO_POINT_LOCATIONS.layerId,
        id: FEATURE_LAYERS_PROPERTIES.GEO_POINT_LOCATIONS.layerKey,
    });
};

export const createStationFeatureLayer = () => {
    return new FeatureLayer({
        title: FEATURE_LAYERS_PROPERTIES.STATIONS.title,
        source: [], // initialize with an empty collection
        renderer: stationIconRenderer,
        objectIdField: 'objectId', // This must be defined when creating a layer from `Graphic` objects
        fields: [
            {
                name: 'stationName',
                alias: 'stationName',
                type: 'string',
            },
            {
                name: 'entityId',
                alias: 'entityId',
                type: 'long',
            },
            {
                name: 'uiState',
                alias: 'uiState',
                type: 'string',
            },
            {
                name: 'iconName',
                alias: 'iconName',
                type: 'string',
            },
        ],
        outFields: ['*'],
        geometryType: 'point',
        labelingInfo: stationIconLabels,
        spatialReference: ESRI_SPATIAL_REFERENCE,
        layerId: FEATURE_LAYERS_PROPERTIES.STATIONS.layerId,
        id: FEATURE_LAYERS_PROPERTIES.STATIONS.layerKey,
    });
};

export const createHospitalFeatureLayer = () => {
    return new FeatureLayer({
        title: FEATURE_LAYERS_PROPERTIES.HOSPITALS.title,
        source: [], // initialize with an empty collection
        renderer: hospitalIconRenderer,
        objectIdField: 'objectId', // This must be defined when creating a layer from `Graphic` objects
        fields: [
            // {
            //     name: 'hospitalName',
            //     alias: 'hospitalName',
            //     type: 'string',
            // },
            {
                name: 'entityId',
                alias: 'entityId',
                type: 'long',
            },
            {
                name: 'uiState',
                alias: 'uiState',
                type: 'string',
            },
            {
                name: 'iconName',
                alias: 'iconName',
                type: 'string',
            },
        ],
        outFields: ['*'],
        geometryType: 'point',
        // hospitals will nto show label on map. If we want to show label in the future. add labelingInfo back
        // labelingInfo: hospitalIconLabels,
        spatialReference: ESRI_SPATIAL_REFERENCE,
        layerId: FEATURE_LAYERS_PROPERTIES.HOSPITALS.layerId,
        id: FEATURE_LAYERS_PROPERTIES.HOSPITALS.layerKey,
    });
};

type CreateCirclesFeatureLayerProps = {
    layerKey: string;
    layerId: number;
    title: string;
    color: number[];
};
export const createCirclesFeatureLayer = ({
    layerKey,
    layerId,
    title,
    color,
}: CreateCirclesFeatureLayerProps): FeatureLayer => {
    return new FeatureLayer({
        title,
        source: [],
        renderer: getCircleSimpleFillRenderer(color),
        objectIdField: 'objectId', // This must be defined when creating a layer from `Graphic` objects
        fields: [
            {
                name: 'entityId',
                alias: 'entityId',
                type: 'long',
            },
        ],
        outFields: ['*'],
        geometryType: 'polygon',
        spatialReference: ESRI_SPATIAL_REFERENCE,
        layerId,
        id: layerKey,
    });
};

export const createUnitsFeatureLayer = (
    enableUnitClusteringOnMap = false,
    clusterRadius = ESRI_MAP_UNIT_LAYER_CLUSTER_RADIUS
): FeatureLayer => {
    return new FeatureLayer({
        title: FEATURE_LAYERS_PROPERTIES.UNITS.title,
        source: [], // initialize with an empty collection
        featureReduction: enableUnitClusteringOnMap
            ? getClusterConfig({
                  labelExpression: "Text($feature.cluster_count, '#,###')",
                  clusterRadius,
              })
            : undefined,
        renderer: unitIconRenderer,
        objectIdField: 'objectId', // This must be defined when creating a layer from `Graphic` objects
        fields: [
            {
                name: 'unitId',
                alias: 'unitId',
                type: 'integer',
            },
            {
                name: 'callSign',
                alias: 'callSign',
                type: 'string',
            },
            {
                name: 'entityId',
                alias: 'entityId',
                type: 'long',
            },
            {
                name: 'iconName',
                alias: 'iconName',
                type: 'string',
            },
            {
                name: 'type',
                alias: 'type',
                type: 'string',
            },
            {
                name: 'uiState',
                alias: 'uiState',
                type: 'string',
            },
            {
                name: 'eventPriorityString',
                alias: 'eventPriorityString',
                type: 'string',
            },
            {
                name: 'agencyTypeGlobalAttrId',
                alias: 'agencyTypeGlobalAttrId',
                type: 'integer',
            },
        ],
        outFields: ['*'],
        labelingInfo: unitIconLabels,
        geometryType: 'point',
        spatialReference: ESRI_SPATIAL_REFERENCE,
        layerId: FEATURE_LAYERS_PROPERTIES.UNITS.layerId,
        id: FEATURE_LAYERS_PROPERTIES.UNITS.layerKey,
    });
};

export const createEventFeatureLayer = (): FeatureLayer => {
    return new FeatureLayer({
        title: FEATURE_LAYERS_PROPERTIES.EVENTS.title,
        source: [], // initialize with an empty collection
        renderer: eventIconRenderer,
        objectIdField: 'objectId', // This must be defined when creating a layer from `Graphic` objects
        fields: [
            {
                name: 'priority',
                alias: 'priority',
                type: 'string',
            },
            {
                name: 'entityId',
                alias: 'entityId',
                type: 'long',
            },
            {
                name: 'iconName',
                alias: 'iconName',
                type: 'string',
            },
            {
                name: 'uiState',
                alias: 'uiState',
                type: 'string',
            },
            {
                name: 'agencyTypeGlobalAttrId',
                alias: 'agencyTypeGlobalAttrId',
                type: 'integer',
            },
        ],
        outFields: ['*'],
        geometryType: 'point',
        labelingInfo: eventIconLabels,
        spatialReference: ESRI_SPATIAL_REFERENCE,
        layerId: FEATURE_LAYERS_PROPERTIES.EVENTS.layerId,
        id: FEATURE_LAYERS_PROPERTIES.EVENTS.layerKey,
    });
};

export const createPhoneCallFeatureLayer = (): FeatureLayer => {
    return new FeatureLayer({
        title: FEATURE_LAYERS_PROPERTIES.PHONE_CALLS.title,
        source: [], // initialize with an empty collection
        renderer: phoneCallIconRenderer,
        objectIdField: 'objectId', // This must be defined when creating a layer from `Graphic` objects
        fields: [
            {
                name: 'entityId',
                alias: 'entityId',
                type: 'long',
            },
            {
                name: 'iconName',
                alias: 'iconName',
                type: 'string',
            },
        ],
        outFields: ['*'],
        geometryType: 'point',
        labelingInfo: [phoneCallIconLabel],
        spatialReference: ESRI_SPATIAL_REFERENCE,
        layerId: FEATURE_LAYERS_PROPERTIES.PHONE_CALLS.layerId,
        id: FEATURE_LAYERS_PROPERTIES.PHONE_CALLS.layerKey,
    });
};

export const createAmlFeatureLayer = (): FeatureLayer => {
    return new FeatureLayer({
        title: FEATURE_LAYERS_PROPERTIES.AML.title,
        source: [], // initialize with an empty collection
        renderer: amlIconRenderer,
        objectIdField: 'objectId', // This must be defined when creating a layer from `Graphic` objects
        fields: [
            {
                name: 'entityId',
                alias: 'entityId',
                type: 'long',
            },
            {
                name: 'iconName',
                alias: 'iconName',
                type: 'string',
            },
            {
                name: 'amlPhoneCallId',
                alias: 'amlPhoneCallId',
                type: 'long',
            },
        ],
        outFields: ['*'],
        geometryType: 'point',
        labelingInfo: [amlIconLabel],
        spatialReference: ESRI_SPATIAL_REFERENCE,
        layerId: FEATURE_LAYERS_PROPERTIES.AML.layerId,
        id: FEATURE_LAYERS_PROPERTIES.AML.layerKey,
    });
};

export const createAmlLocationHistoryFeatureLayer = (): FeatureLayer => {
    return new FeatureLayer({
        title: FEATURE_LAYERS_PROPERTIES.AML_LOCATION_HISTORY.title,
        source: [],
        renderer: amlLocationHistoryIconRenderer,
        objectIdField: 'objectId', // This must be defined when creating a layer from `Graphic` objects
        fields: [
            {
                name: 'entityId',
                alias: 'entityId',
                type: 'long',
            },
            {
                name: 'iconName',
                alias: 'iconName',
                type: 'string',
            },
            {
                name: 'index',
                alias: 'index',
                type: 'integer',
            },
        ],
        outFields: ['*'],
        geometryType: 'point',
        spatialReference: ESRI_SPATIAL_REFERENCE,
        layerId: FEATURE_LAYERS_PROPERTIES.AML_LOCATION_HISTORY.layerId,
        id: FEATURE_LAYERS_PROPERTIES.AML_LOCATION_HISTORY.layerKey,
    });
};

export const createPolylineFeatureLayer = (): FeatureLayer => {
    return new FeatureLayer({
        title: FEATURE_LAYERS_PROPERTIES.POLYLINES.title,
        source: [], // initialize with an empty collection
        renderer: polylineRenderer,
        objectIdField: 'objectId', // This must be defined when creating a layer from `Graphic` objects
        fields: [
            {
                name: 'entityId',
                alias: 'entityId',
                type: 'long',
            },
            {
                name: 'type',
                alias: 'type',
                type: 'string',
            },
        ],
        outFields: ['*'],
        geometryType: 'polyline',
        spatialReference: ESRI_SPATIAL_REFERENCE,
        layerId: FEATURE_LAYERS_PROPERTIES.POLYLINES.layerId,
        id: FEATURE_LAYERS_PROPERTIES.POLYLINES.layerKey,
    });
};

export const createNearbyGeoCautionsFeatureLayer = (): FeatureLayer => {
    return new FeatureLayer({
        title: FEATURE_LAYERS_PROPERTIES.NEARBY_GEO_CAUTIONS.title,
        source: [], // initialize with an empty collection
        renderer: nearbyGeoCautionsIconRenderer,
        objectIdField: 'objectId', // This must be defined when creating a layer from `Graphic` objects
        fields: [
            {
                name: 'entityId',
                alias: 'entityId',
                type: 'long',
            },
            {
                name: 'iconName',
                alias: 'iconName',
                type: 'string',
            },
        ],
        outFields: ['*'],
        geometryType: 'point',
        spatialReference: ESRI_SPATIAL_REFERENCE,
        layerId: FEATURE_LAYERS_PROPERTIES.NEARBY_GEO_CAUTIONS.layerId,
        id: FEATURE_LAYERS_PROPERTIES.NEARBY_GEO_CAUTIONS.layerKey,
    });
};

export const createInitialGeoPointPinFeatureLayer = (): FeatureLayer => {
    return new FeatureLayer({
        title: FEATURE_LAYERS_PROPERTIES.INITIAL_GEO_POINT_PIN.title,
        source: [], // initialize with an empty collection
        renderer: initialGeoPointPinRenderer,
        objectIdField: 'objectId', // This must be defined when creating a layer from `Graphic` objects
        fields: [
            {
                name: 'entityId',
                alias: 'entityId',
                type: 'long',
            },
        ],
        outFields: ['*'],
        geometryType: 'point',
        spatialReference: ESRI_SPATIAL_REFERENCE,
        layerId: FEATURE_LAYERS_PROPERTIES.INITIAL_GEO_POINT_PIN.layerId,
        id: FEATURE_LAYERS_PROPERTIES.INITIAL_GEO_POINT_PIN.layerKey,
    });
};
